import React, { useCallback, useEffect, useState } from 'react';

import ActualVsBudgetReportDetailedTable from '../../actual_vs_budget_report/ActualVsBudgetReportDetailedTable';
import ActualVsBudgetReportSummaryTable from '../../actual_vs_budget_report/ActualVsBudgetReportSummaryTable';

import ArchivedDepartmentDetailedTable from './ArchivedDepartmentDetailedTable';

import { apiClient } from 'Helpers/network';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

export const MODULE_TYPE_EVENT = 'event';

/**
 * A stored report's own provenance, in the shape ActualVsBudgetReportHeader's
 * `storedReport` prop expects. This view renders the tables outside the report
 * provider — there is no configuration form behind an archived report — so the
 * header and the comparison column labels are fed from the report's persisted
 * parameters instead, with the version names already resolved server-side.
 *
 * The column preset and the "Customise columns" overrides are stored with the
 * report, so an archived report renders the columns it was generated with rather
 * than whatever the configuration screen happens to show now. "Hide rows with no
 * values" is still pure client-side state that is never stored, so the header
 * drops that chip rather than inventing a value.
 */
const buildStoredReport = (report, entityName) => ({
    name: report?.name || '',
    budget_name: report?.budget_name || '',
    comparison_budget_name: report?.comparison_budget_name || '',
    start_date: report?.start_date || null,
    end_date: report?.end_date || null,
    report_level: report?.report_level || null,
    generated_at: report?.generated_at || null,
    // Keys mirror ActualCostsReportResource. A report generated before these were
    // stored has neither, which resolves to the standard preset with no
    // overrides — exactly how it rendered when it was generated.
    column_preset: report?.column_preset || null,
    visible_columns: report?.visible_columns || null,
    entityName,
});

const ArchivedReportView = ({ reportId, showUrl }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportType, setReportType] = useState(null);
    const [summary, setSummary] = useState(null);
    const [details, setDetails] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [module, setModule] = useState(null);
    const [storedReport, setStoredReport] = useState(null);

    useEffect(() => {
        fetchReport();
    }, [reportId]);

    const fetchReport = () => {
        setLoading(true);
        setError(null);

        const url = showUrl.replace('ACTUAL_COSTS_REPORT_ID', reportId);

        apiClient
            .get(url)
            .then(({ data }) => {
                if (!data.cached_report) {
                    setError($t('Report data is not available.'));
                    setLoading(false);
                    return;
                }

                setModule(data.report.module);

                const { metadata, data: reportData } = data.cached_report;
                const type = metadata?.report_type || 'summary';
                setReportType(type);

                setStoredReport(buildStoredReport(data.report, data.entityName));

                if (type === 'summary') {
                    if (reportData?.summary) {
                        setSummary(reportData.summary);
                    } else {
                        setError($t('Summary data is not available.'));
                    }
                } else if (type === 'detailed') {
                    const { headings, data: detailedData, dates, collapse_options } = reportData;

                    if (collapse_options) {
                        setExpanded(collapse_options);
                    }

                    const datesObj = {};
                    if (dates) {
                        dates.forEach((date) => {
                            datesObj[date] = true;
                        });
                    }

                    setDetails({
                        datesArray: dates || [],
                        dates: datesObj,
                        headings: headings || [],
                        data: detailedData,
                    });
                }

                setLoading(false);
            })
            .catch((e) => {
                console.error('Error fetching report:', e);
                setError($t('Failed to load report data.'));
                setLoading(false);
            });
    };

    const toggleExpansion = useCallback(
        (type, ...keys) => {
            const [department, costcenter, code, workforce, project] = keys;
            const options = JSON.parse(JSON.stringify(expanded));

            if (code) {
                if (costcenter) {
                    if (project) {
                        options[type][department].costcenters[costcenter].projects[project].codes[code].expanded =
                            !options[type][department].costcenters[costcenter].projects[project].codes[code].expanded;
                    } else {
                        options[type][department].costcenters[costcenter].codes[code].expanded =
                            !options[type][department].costcenters[costcenter].codes[code].expanded;
                    }
                } else {
                    if (project) {
                        options[type][department].projects[project].codes[code].expanded = !options[type][department].projects[project].codes[code].expanded;
                    } else {
                        options[type][department].codes[code].expanded = !options[type][department].codes[code].expanded;
                    }
                }
            } else if (costcenter) {
                if (workforce) {
                    options[type][department].costcenters[costcenter].workforce.expanded =
                        !options[type][department].costcenters[costcenter].workforce.expanded;
                } else {
                    if (project) {
                        options[type][department].costcenters[costcenter].projects[project].expanded =
                            !options[type][department].costcenters[costcenter].projects[project].expanded;
                    } else {
                        options[type][department].costcenters[costcenter].expanded = !options[type][department].costcenters[costcenter].expanded;
                    }
                }
            } else if (department) {
                if (workforce) {
                    options[type][department].workforce.expanded = !options[type][department].workforce.expanded;
                } else {
                    if (project) {
                        options[type][department].projects[project].expanded = !options[type][department].projects[project].expanded;
                    } else {
                        options[type][department].expanded = !options[type][department].expanded;
                    }
                }
            }

            setExpanded(options);
        },
        [expanded],
    );

    const toggleAllExpansion = useCallback(
        (expand = true) => {
            const options = JSON.parse(JSON.stringify(expanded));

            if (options.expense) {
                Object.entries(options.expense).forEach(([expenseKey, expenseValue]) => {
                    options.expense[expenseKey].expanded = expand;

                    if (expenseValue.projects) {
                        Object.entries(expenseValue.projects).forEach(([projectKey, projectValue]) => {
                            options.expense[expenseKey].projects[projectKey].expanded = expand;

                            if (projectValue.codes) {
                                Object.entries(projectValue.codes).forEach(([projectCodeKey]) => {
                                    options.expense[expenseKey].projects[projectKey].codes[projectCodeKey].expanded = expand;
                                });
                            }
                        });
                    }

                    if (expenseValue.codes) {
                        Object.entries(expenseValue.codes).forEach(([codeKey]) => {
                            options.expense[expenseKey].codes[codeKey].expanded = expand;
                        });
                    }

                    if (expenseValue.costcenters) {
                        Object.entries(expenseValue.costcenters).forEach(([costcenterKey, costcenterValue]) => {
                            options.expense[expenseKey].costcenters[costcenterKey].expanded = expand;

                            if (costcenterValue.projects) {
                                Object.entries(costcenterValue.projects).forEach(([projectCcKey, projectCcValue]) => {
                                    options.expense[expenseKey].costcenters[costcenterKey].projects[projectCcKey].expanded = expand;

                                    if (projectCcValue.codes) {
                                        Object.entries(projectCcValue.codes).forEach(([projectCcCodeKey]) => {
                                            options.expense[expenseKey].costcenters[costcenterKey].projects[projectCcKey].codes[projectCcCodeKey].expanded =
                                                expand;
                                        });
                                    }
                                });
                            }

                            if (costcenterValue.codes) {
                                Object.entries(costcenterValue.codes).forEach(([codeKey]) => {
                                    options.expense[expenseKey].costcenters[costcenterKey].codes[codeKey].expanded = expand;
                                });
                            }
                        });
                    }
                });
            }

            if (options.revenue) {
                Object.entries(options.revenue).forEach(([revenueKey, revenueValue]) => {
                    options.revenue[revenueKey].expanded = expand;

                    if (revenueValue.codes) {
                        Object.entries(revenueValue.codes).forEach(([codeKey]) => {
                            options.revenue[revenueKey].codes[codeKey].expanded = expand;
                        });
                    }

                    if (revenueValue.costcenters) {
                        Object.entries(revenueValue.costcenters).forEach(([costcenterKey, costcenterValue]) => {
                            options.revenue[revenueKey].costcenters[costcenterKey].expanded = expand;

                            if (costcenterValue.codes) {
                                Object.entries(costcenterValue.codes).forEach(([codeKey]) => {
                                    options.revenue[revenueKey].costcenters[costcenterKey].codes[codeKey].expanded = expand;
                                });
                            }
                        });
                    }
                });
            }

            setExpanded(options);
        },
        [expanded],
    );

    if (loading) {
        return (
            <div className="tw-py-12">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="tw-text-center tw-py-8 tw-text-red-500">
                <p>{error}</p>
            </div>
        );
    }

    if (reportType === 'summary' && summary) {
        return <ActualVsBudgetReportSummaryTable summary={summary} module={MODULE_TYPE_EVENT} storedReport={storedReport} />;
    }

    if (reportType === 'detailed' && details) {
        if (module === 'costcenter' || module === 'department') {
            return (
                <ArchivedDepartmentDetailedTable
                    details={details}
                    expanded={expanded}
                    module={module}
                    onToggleExpansion={toggleExpansion}
                    onToggleAllExpansion={toggleAllExpansion}
                    storedReport={storedReport}
                />
            );
        }

        return (
            <ActualVsBudgetReportDetailedTable
                details={details}
                expanded={expanded}
                module={MODULE_TYPE_EVENT}
                onToggleExpansion={toggleExpansion}
                onToggleAllExpansion={toggleAllExpansion}
                storedReport={storedReport}
            />
        );
    }

    return (
        <div className="tw-text-center tw-py-8 tw-text-gray-500">
            <p>{$t('No report data available.')}</p>
        </div>
    );
};

export default ArchivedReportView;
